import React, { useState, useEffect } from 'react';

// Material
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

// Components
import TextField from '../../../components/inputs/input';
import Dropdown from '../../../components/inputs/dropdown';
import ModalSuccess from '../../../components/modal/submit';
import AutoComplete from '../../../components/inputs/Autocomplete';
import Address from './address/index';

import useStyles from "./styles";
import { Genders, GetDocumentType, DocumentTextHelp, DocumentType, TaxConditionType, country, GetTaxCondition, countriesEnum, GetTaxConditionLabel } from '../../../models/EnumEntity';
import { utils } from '../../../utils/utils';
import IsDocumentValid, { DocumentMessageError } from '../../../utils/documentCheck';

import { IibbCategories } from '../../../models/IibbCategories';

// Hooks
import useCustomer from '../../../hooks/customers/useCustomer';
import useCountries from '../../../hooks/countries/useCountries';
import useNotify from '../../../hooks/notify/index';
import TaxConfig from './taxConfig';
import useUser from '../../../hooks/user/useUser';
import InputEmail from '../../../components/inputs/input/email';

const CustomerCreate = ({ onAccept, onCancel, disabled, customerId }) => {
    const { branch} = useUser();
    const { classes } = useStyles();
    const { customer, edit, save } = useCustomer(customerId);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [operationCountry, setOperationCountry] = useState('');
    const { countries, loading: loadingCountries } = useCountries();

    const [submitOpen, setSubmitOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const { Notify, variants } = useNotify();

    useEffect(() => {
        if (countries.length > 0 && customer) {
            const country = countries.find(c => c.isoAlpha2Code === customer?.country) || countries.find(c => c.isoAlpha2Code === operationCountry);
            setSelectedCountry(country);
            if (!customer.country) {
                edit({ country: country?.isoAlpha2Code })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countries, customer])

    useEffect(() => {
        if (branch)
            setOperationCountry(customer?.operationCountry ?? branch.country);
        if (branch && customer && customer.branchId === undefined)
            edit({ branchId: branch.id })
    }, [branch, customer])

    useEffect(() => {
        if (!customer.operationCountry && operationCountry)
            edit({ operationCountry });
    }, [customer.operationCountry, edit, operationCountry]);

    const handleError = (event) => {
        setSubmitOpen(false);
        setSuccess(false);
        setError(false);
    }

    const handleSave = async () => {
        const docType = GetDocumentType(customer?.operationCountry, selectedCountry?.isoAlpha2Code)
            .find(s => s.value === customer.documentType);

        if (!docType.acceptCharacters && isNaN(customer?.documentNumber)) {
            Notify("El número de documento no puede contener caracteres, a menos que sea de tipo Pasaporte", variants.ERROR);
            return;
        }

        if (customer.taxCondition === TaxConditionType.RegisteredManager &&
            customer.addresses?.length === 0) {
            Notify(`El cliente debe registrar al menos una dirección cuando su condición fiscal es 'Responsable Inscripto'`, variants.ERROR);
            return;
        }

        const taxConditionAR = [TaxConditionType.Exempt,
        TaxConditionType.RegisteredManager,
        TaxConditionType.SingleTaxSystemPayer];

        if (customer?.operationCountry === countriesEnum.AR &&
            taxConditionAR.includes(customer?.taxCondition)) {
            if (customer?.documentType !== DocumentType.CUIT) {
                Notify(`El cliente es de tipo '${GetTaxConditionLabel(customer.taxCondition)}' y debe tener asociado documento de tipo CUIT.`, variants.ERROR);
                return;
            }
            const iibb = customer?.taxConfig?.iibb;
            if (!iibb || iibb?.iibb <= 0) {
                Notify(`El cliente es de tipo '${GetTaxConditionLabel(customer.taxCondition)}' y debe completar los datos de Ingresos brutos.`, variants.ERROR);
                return;
            }

            if (iibb?.category === IibbCategories.ConvenioMultilateral && iibb?.convenios?.length === 0) {
                Notify("Se requiere rellenar la solapa de Convenios Multilaterales", variants.ERROR);
                return;
            }

            if (iibb?.category === IibbCategories.Exento && iibb?.exemptions?.length === 0) {
                Notify("Se requiere de una exención cargada", variants.ERROR);
                return;
            }
        }

        if (!IsDocumentValid(customer.documentType, customer.documentNumber)) {
            Notify(DocumentMessageError[customer.documentType], variants.ERROR);
            return;
        }

        setSubmitOpen(true);

        try {
            const res = await save(customer);
            setSuccess(!!res);
        } catch (error) {
            Notify(`${error.message}`, 'error');
            setError(true);
        }
    };

    const modalOnSucces = () => {
        if (onAccept)
            onAccept(customer);
    }

    const handleChange = (event) =>
        edit({ [event.target.name]: event.target.value });

    const handleChangeProps = (value, name) =>
        edit({ [name]: value });

    const handleChangeTrim = (event) =>
        edit({ [event.target.name]: event.target.value.trim() });

    const handleChangeDocType = (event) => {
        const documentType = event.target.value !== '-1'
            ? event.target.value
            : '';

        const taxCondition = GetTaxCondition(documentType);

        edit({
            [event.target.name]: documentType,
            taxCondition: taxCondition[0]?.value || ''
        })
    }

    const handleChangeName = (event) => {
        const formatName = customer.taxCondition === TaxConditionType.FinalConsumer || customer.taxCondition === TaxConditionType.ExteriorClient
            ? utils.formatFinalConsumerName
            : utils.formatCompanyName

        edit({ [event.target.name]: formatName(event.target.value) })
    }

    const handleChangeAutocomplete = (filter, value) => {
        edit({
            [filter]: value?.isoAlpha2Code || countries[0]?.isoAlpha2Code,
            documentType: filter !== 'country'
                ? customer?.documentType
                : ''
        });
    }

    const isFormValid = () => {
        if (!customer?.firstName)
            return false;

        if (!customer?.country)
            return false;

        if (!customer?.documentType)
            return false;

        if (!IsDocumentValid(customer.documentType, customer.documentNumber))
            return false;

        if (!customer?.taxCondition)
            return false;

        if (utils.validateMail(customer?.email))
            return false;

        return true;
    }

    const handleSwitchChange = async (event) => {
        edit({ [event.target.name]: event.target.checked });
    }

    return (
        <>
            <ModalSuccess
                open={submitOpen}
                success={success}
                intervale={500}
                onSuccess={modalOnSucces}
                successText={`Cliente ${customer.id ? "modificado" : "creado"} con éxito`}
                error={error}
                onError={handleError}
                errorText={`No se pudo ${customer.id ? "modificar" : "crear"} el cliente`}
            />
            <Grid container spacing={1} justifyContent='center'>
                <Grid size={12}>
                    <Paper className={classes.paper}>
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label='Apellido'
                                    name='lastName'
                                    value={customer?.lastName || ""}
                                    onChange={handleChangeName}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <TextField
                                    fullWidth
                                    required
                                    label='Nombre o Razon social'
                                    name='firstName'
                                    value={customer?.firstName || ""}
                                    onChange={handleChangeName}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <Dropdown
                                    fullWidth
                                    required={customer?.taxCondition !== TaxConditionType.RegisteredManager}
                                    name='gender'
                                    label='Genero'
                                    value={customer?.gender || ""}
                                    values={Genders}
                                    onChange={handleChange}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <AutoComplete
                                    name='country'
                                    fullWidth
                                    disabled={disabled || customer.id}
                                    loading={loadingCountries}
                                    onChange={(e, value) => handleChangeAutocomplete('country', value)}
                                    options={countries}
                                    value={selectedCountry}
                                    getOptionLabel={(option) => option?.name || ''}
                                    getOptionSelected={(option, value) => (option?.isoAlpha2Code === value?.isoAlpha2Code)}
                                    TextProps={{
                                        label: 'Pais',
                                        required: true,
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <Dropdown
                                    fullWidth
                                    required
                                    name='documentType'
                                    label='Tipo de documento'
                                    value={customer?.documentType || '-1'}
                                    values={GetDocumentType(customer?.operationCountry, selectedCountry?.isoAlpha2Code)}
                                    onChange={handleChangeDocType}
                                    disabled={disabled || customer.id || !selectedCountry?.isoAlpha2Code}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <Dropdown
                                    fullWidth
                                    required
                                    name='operationCountry'
                                    label='País de operación (sucursal actual)'
                                    value={operationCountry || ''}
                                    values={country}
                                    disabled
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <TextField
                                    fullWidth
                                    required
                                    label='Numero de documento'
                                    name='documentNumber'
                                    helperText={DocumentTextHelp(customer?.documentType)}
                                    value={customer?.documentNumber || ""}
                                    onChange={handleChangeTrim}
                                    disabled={disabled || customer.id}
                                    error={
                                        !!customer?.documentType &&
                                        !!customer?.documentNumber &&
                                        !IsDocumentValid(customer.documentType, customer.documentNumber)
                                    }
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <Dropdown
                                    fullWidth
                                    required
                                    name='taxCondition'
                                    label='Condición impositiva ante I.V.A.'
                                    value={customer?.taxCondition || ""}
                                    values={GetTaxCondition(customer?.documentType)}
                                    onChange={handleChange}
                                    disabled={disabled || !customer?.documentType }
                                />
                                {
                                    customer &&
                                    customer.operationCountry === 'AR' &&
                                    <TaxConfig customer={customer} disabled={disabled} />
                                }
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <InputEmail
                                    value={customer?.email || ""}
                                    onChange={(value) => handleChangeProps(value, "email")}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label='Telefono / Celular'
                                    name='phone'
                                    value={customer?.phone || ""}
                                    onChange={handleChange}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}
                            >
                            <TextField
                                fullWidth
                                label='Fecha de Nacimiento'
                                name='birthDate'
                                type='date'
                                value={customer?.birthDate ? customer.birthDate.split('T')[0] : ''}
                                onChange={handleChange}
                                disabled={disabled}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            </Grid>
                            <Box component={Grid} item xs={12} sm={6} display='flex' alignItems='center'>
                                <Typography>{`Newsletter ${customer.activeNewsletter ? 'Activado' : 'Desactivado'}`}</Typography>
                                <Switch
                                    color='primary'
                                    name='activeNewsletter'
                                    onChange={handleSwitchChange}
                                    checked={customer.activeNewsletter}
                                    disabled={disabled}
                                />
                            </Box>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid size={12}>
                    <Address
                        customer={customer}
                        disabled={disabled}
                    />
                </Grid>
                {!disabled &&
                    <Grid size={12}>
                        <Paper className={classes.paper}>
                            <Grid container justifyContent='space-between' size={12}>
                                <Button
                                    color="primary"
                                    variant="text"
                                    onClick={onCancel}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    name="createCustomerSaveButton"
                                    disabled={!isFormValid() || customer?.documentNumber === '11111111'}
                                    onClick={handleSave}
                                    color="primary"
                                    variant='outlined'
                                    autoFocus
                                >
                                    Guardar
                                </Button>
                            </Grid>
                        </Paper>
                    </Grid>}
            </Grid>
        </>
    );
}

export default CustomerCreate;